import type { HandoverType } from '@sivan/shared';
import { useId } from 'react';
import type { Review, ReviewEntry, ReviewTarget } from './review';

// "סיכום הבדיקה" (H-Next): rooms and general checks completion, open findings, findings awaiting
// reinspection and missing information, each linking to where it is completed. A review only: no
// signature, official document or closing.

function EntryList({
  entries,
  label,
  onGo,
}: {
  readonly entries: readonly ReviewEntry[];
  readonly label: string;
  readonly onGo: (target: ReviewTarget) => void;
}) {
  return (
    <ul className="review-list" aria-label={label}>
      {entries.map((e) => (
        <li key={e.key}>
          <button
            type="button"
            className="review-list__go"
            onClick={() => {
              onGo(e.target);
            }}
          >
            <span>{e.text}</span>
            <span aria-hidden="true">←</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function InspectionReview({
  review,
  type,
  onGo,
}: {
  readonly review: Review;
  readonly type: HandoverType;
  readonly onGo: (target: ReviewTarget) => void;
}) {
  const id = useId();
  const roomsDone = review.rooms.filter((r) => r.total > 0 && r.checked === r.total).length;
  return (
    <div className="inspection-review">
      <p
        className={`notice ${review.complete ? 'notice--success' : 'notice--warning'}`}
        role="status"
      >
        {review.complete
          ? 'כל הפריטים נבדקו ולא חסר מידע.'
          : 'הבדיקה עדיין לא הושלמה. לחצו על פריט כדי לעבור אליו.'}{' '}
        הסיכום אינו חתימה ואינו סוגר את המסירה.
      </p>
      <dl className="review-stats">
        <div>
          <dt>חדרים שהושלמו</dt>
          <dd>
            {review.rooms.length === 0
              ? 'אין רשימת בדיקה'
              : `${String(roomsDone)} מתוך ${String(review.rooms.length)}`}
          </dd>
        </div>
        <div>
          <dt>בדיקות כלליות</dt>
          <dd>
            {review.checks.total === 0
              ? 'אין'
              : `${String(review.checks.checked)} מתוך ${String(review.checks.total)}`}
          </dd>
        </div>
        <div>
          <dt>ליקויים פתוחים</dt>
          <dd>{review.openFindings}</dd>
        </div>
        {type === 'final' && (
          <>
            <div>
              <dt>ממתינים לבדיקה חוזרת</dt>
              <dd>{review.awaiting.length}</dd>
            </div>
            <div>
              <dt>עדיין לא תקין</dt>
              <dd>{review.stillDefective}</dd>
            </div>
          </>
        )}
      </dl>
      {review.openFindings > 0 && (
        <button
          type="button"
          className="button button--small"
          onClick={() => {
            onGo({ kind: 'section', section: 'findings' });
          }}
        >
          מעבר לליקויים הפתוחים
        </button>
      )}
      {review.incomplete.length > 0 && (
        <section aria-labelledby={`${id}-incomplete`}>
          <h3 id={`${id}-incomplete`} className="review-title">
            טרם נבדקו ({review.incomplete.length})
          </h3>
          <EntryList entries={review.incomplete} label="פריטים שטרם נבדקו" onGo={onGo} />
        </section>
      )}
      {review.awaiting.length > 0 && (
        <section aria-labelledby={`${id}-awaiting`}>
          <h3 id={`${id}-awaiting`} className="review-title">
            ממתינים לבדיקה חוזרת ({review.awaiting.length})
          </h3>
          <EntryList entries={review.awaiting} label="ליקויים לבדיקה חוזרת" onGo={onGo} />
        </section>
      )}
      {review.missing.length > 0 && (
        <section aria-labelledby={`${id}-missing`}>
          <h3 id={`${id}-missing`} className="review-title">
            מידע חסר ({review.missing.length})
          </h3>
          <EntryList entries={review.missing} label="מידע חסר" onGo={onGo} />
        </section>
      )}
    </div>
  );
}
